import { ConfirmationModalContextWrapper } from 'Components/ChatLayoutHeader/contexts/ConfirmationModalContextWrapper';
import SmallSidebarMenu from 'Components/SmallSidebarMenu';
import {
  STORE_NOTIFICATION,
  STORE_PERSON,
  STORE_CONFIG,
  STORE_PHONE_CALL,
  STORE_PREFERENCE,
  STORE_UI,
  STORE_MESSAGE,
  STORE_CONVERSATION,
} from 'Constants/stores';
import { usePreferences } from 'Hooks/preferences/usePreferences';
import { inject, MobXProviderContext, observer } from 'mobx-react';
import Softphone from 'Modules/softphone/components/legacy/Softphone';
import { SoftphoneAnnouncer } from 'Modules/softphone/components/SoftphoneAnnouncer';
import { SoftphonePopout } from 'Modules/softphone/components/SoftphonePopout';
import { useSoftphoneCoordinatorStore } from 'Modules/softphone/store';
import { useUIStore } from 'Modules/ui/index.store';
import { getIsSmallScreen } from 'Modules/ui/secondaryMenu/utils';
import React, { useEffect } from 'react';
import { Outlet, UIMatch, useMatches } from 'react-router';
import { LiveAnnouncer } from 'Services/announcementService';
import { RootStoreProps } from 'Stores/RootStore.types';
import { SidebarProvider } from '~/lib/ui/sidebar';
import SidePanelManager from '../SidePanelManager';
import { Styled } from './index.styles';
import { ILayoutProps } from './interfaces';

const Layout = (props: ILayoutProps) => {
  const { ui, person } = React.useContext<RootStoreProps>(MobXProviderContext);

  const { sidePanelCustomWidth, isSecondaryMenuOpen, setIsSecondaryMenuOpen } =
    useUIStore();

  const {
    preferences: { floatingSoftphone: isSoftphoneFixed },
  } = usePreferences();

  /**
   * Softphone Coordination Legacy/New
   * Read softphone preference from Zustand store with per-user persistence.
   * Determines which softphone component to render in the layout.
   */
  const personId = person?.loggedInPersonId;

  const softphoneCoordinatorStore = useSoftphoneCoordinatorStore(personId);
  const isNewSoftphoneEnabled = softphoneCoordinatorStore(
    (state) => state.isNewSoftphoneEnabled
  );

  const matches = useMatches() as {
    handle: { title: string };
  }[] &
    Omit<UIMatch, 'handle'>[];

  useEffect(() => {
    const baseTitle = 'Broadvoice Communicator';
    // Gather titles from `handle` properties in the route match hierarchy
    const routeTitles = matches
      .map((match) => match.handle?.title)
      .filter(Boolean);

    const unreadCounter =
      ui.totalUnreadMessagesCount > 0
        ? `(${ui.totalUnreadMessagesCount}) `
        : '';

    const pageTitle =
      routeTitles.length > 0
        ? `${unreadCounter}${baseTitle} - ${routeTitles.join(' / ')}`
        : `${unreadCounter}${baseTitle}`;

    document.title = pageTitle;
  }, [matches, ui.totalUnreadMessagesCount, ui]);

  const renderTouchBackground =
    ui.isTouchSupported() && isSecondaryMenuOpen && getIsSmallScreen();

  return (
    <>
      <SmallSidebarMenu />
      <SidebarProvider defaultOpen={false} width={sidePanelCustomWidth}>
        <ConfirmationModalContextWrapper>
          <Outlet />
          <SidePanelManager isNewSoftphoneEnabled={isNewSoftphoneEnabled} />
          {/* App-wide live regions for screen reader announcements */}
          <LiveAnnouncer />
          {/* Above the widget, so it outlives it when a call ends */}
          {isNewSoftphoneEnabled && <SoftphoneAnnouncer />}
          {/* Softphone popout for floating mode */}
          {!isSoftphoneFixed &&
            (isNewSoftphoneEnabled ? <SoftphonePopout /> : <Softphone />)}
        </ConfirmationModalContextWrapper>
      </SidebarProvider>
      <Styled.TouchBackground
        $renderTouchBg={renderTouchBackground}
        onClick={() => {
          setIsSecondaryMenuOpen(false);
        }}
      />
    </>
  );
};

export default inject(
  STORE_NOTIFICATION,
  STORE_PERSON,
  STORE_CONFIG,
  STORE_PHONE_CALL,
  STORE_PREFERENCE,
  STORE_UI,
  STORE_MESSAGE,
  STORE_CONVERSATION
)(observer(Layout));
